import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { ReactNode } from 'react';
import Image from 'src/components/image';
import Scrollbar from 'src/components/scrollbar';
import { TablePaginationCustom } from 'src/components/table';
import { translateKey } from 'src/locales/translate';
import {
  TAB_TABLE_CONTAINER_SX,
  TAB_TABLE_PAGINATION_SX,
  TAB_TABLE_ROWS_PER_PAGE_OPTIONS,
  TAB_TABLE_SCROLL_BODY_SX,
} from 'src/theme/tableContainer';
import { GetProfileMedicationsDto } from 'woofwoof-api';
import MedicationStatusBadge from 'src/components/medication-status-badge/MedicationStatusBadge';
import {
  formatDate,
  formatDose,
  formatDuration,
  formatFrequency,
  formatStopReason,
  formatStrength,
} from './medication-form/medicationDisplay';

interface Row extends GetProfileMedicationsDto {
  index: number;
}

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  render: (row: Row) => ReactNode;
}

interface ProfileMedicationsTableProps {
  onRowClick?: (id: string) => void;
  /** Opens the prescribing visit, the way the history table opens a row. */
  onVisitClick?: (visitId: string) => void;
  data?: GetProfileMedicationsDto[];
  disableRowClick?: boolean;
  /** The patient this table belongs to — used to link a row to its visit. */
  patientId?: string;
  page: number;
  rowsPerPage: number;
  total: number;
  onPageChange: (event: unknown, page: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const ELLIPSIS_SX = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: 200,
};

export default function ProfileMedicationsTable({
  onRowClick,
  onVisitClick,
  data,
  page,
  rowsPerPage,
  total,
  onPageChange,
  onRowsPerPageChange,
  disableRowClick = false,
  patientId,
}: ProfileMedicationsTableProps) {
  const tableData: Row[] =
    data?.map((medication, index) => ({ ...medication, index })) || [];

  const handleOnRowClick = (id: string) => onRowClick?.(id);

  const COLUMNS: Column[] = [
    {
      id: 'name',
      label: 'Nazwa',
      render: (row) => (
        <Stack>
          <Typography
            variant="subtitle2"
            sx={{ ...ELLIPSIS_SX, maxWidth: 250 }}
          >
            {row.name}
          </Typography>
          {row.preparationType && (
            <Typography variant="caption" color="text.secondary">
              {row.preparationType === 'Ludzki' ? 'Ludzki' : 'Weterynaryjny'}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      // The table is sorted newest-first on this, so it has to be visible:
      // the only other date on screen is the prescribing visit's, which runs
      // in its own order and made the list look unsorted.
      id: 'createdAt',
      label: translateKey('medication.table.added'),
      render: (row) =>
        row.createdAt ? (
          <Typography variant="body2" data-testid="medication-created-at">
            {format(new Date(row.createdAt), 'dd/MM/yyyy H:mm')}
          </Typography>
        ) : null,
    },
    {
      id: 'strength',
      label: translateKey('medication.table.strength'),
      render: (row) => (
        <Typography variant="body2">{formatStrength(row)}</Typography>
      ),
    },
    {
      id: 'dosing',
      label: translateKey('medication.table.dosing'),
      // Two lines, like the name column: the dose is what the eye goes to, the
      // frequency qualifies it.
      render: (row) => {
        const dose = formatDose(row);
        const frequency = formatFrequency(row);
        return (
          <Stack>
            <Typography variant="subtitle2">{dose}</Typography>
            {frequency && (
              <Typography variant="caption" color="text.secondary">
                {frequency}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      id: 'duration',
      label: translateKey('medication.table.duration'),
      render: (row) => (
        <Typography variant="body2">{formatDuration(row)}</Typography>
      ),
    },
    {
      id: 'status',
      label: translateKey('medication.table.status'),
      render: (row) => {
        const stoppedAt = formatDate(row.endedAt);
        const reason = formatStopReason(row);
        return (
          <Stack alignItems="flex-start" gap={0.25}>
            <MedicationStatusBadge status={row.status} />
            {row.status === 'STOPPED' && (stoppedAt || reason) && (
              <Typography variant="caption" color="text.secondary">
                {[stoppedAt, reason].filter(Boolean).join(' · ')}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      id: 'sourceVisit',
      label: translateKey('medication.table.sourceVisit'),
      render: (row) => {
        // Not every medication comes from a visit — an owner adds their own,
        // and a treatment can attach one. Those rows simply have no date.
        if (!row.sourceVisitId || !row.sourceVisitDate) return null;
        return (
          <Typography
            variant="body2"
            data-testid="medication-source-visit"
            onClick={(event) => {
              event.stopPropagation();
              onVisitClick?.(row.sourceVisitId as string);
            }}
            sx={{
              textDecoration: 'underline',
              cursor: 'pointer',
              width: 'fit-content',
            }}
          >
            {format(new Date(row.sourceVisitDate), 'dd/MM/yyyy H:mm')}
          </Typography>
        );
      },
    },
  ];

  const HeaderCells = COLUMNS.map((column) => (
    <TableCell
      key={column.id}
      align={column.align}
      style={{ minWidth: column.minWidth }}
    >
      {column.label}
    </TableCell>
  ));

  const TableHeader = (
    <Table stickyHeader>
      <TableHead>
        <TableRow>{HeaderCells}</TableRow>
      </TableHead>
    </Table>
  );

  const FullTable = (
    <Box sx={TAB_TABLE_SCROLL_BODY_SX}>
      <Scrollbar>
        <Table stickyHeader>
          <TableHead>
            <TableRow>{HeaderCells}</TableRow>
          </TableHead>
          <TableBody>
            {/* Already this page's rows: the server did the slicing. */}
            {tableData.map((row) => (
              <TableRow
                hover
                key={`${row.id}-${row.index}`}
                data-testid="medication-row"
                data-medication-id={row.id}
                data-medication-status={row.status}
                // A stopped course stays readable but steps back: the eye should
                // land on what the patient is taking now.
                sx={row.status === 'STOPPED' ? { opacity: 0.6 } : undefined}
                onClick={() => !disableRowClick && handleOnRowClick(row.id)}
              >
                {COLUMNS.map((column) => (
                  <TableCell key={column.id} align={column.align}>
                    {column.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
    </Box>
  );

  return (
    <>
      <TableContainer sx={TAB_TABLE_CONTAINER_SX}>
        {tableData.length > 0 ? (
          FullTable
        ) : (
          <>
            {TableHeader}
            <Stack
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              flex="1"
              gap={2}
            >
              <Image
                sx={{ width: '200px' }}
                alt="empty medications"
                src="/assets/images/empty_table.svg"
              />
              <Typography>Brak lekarstw</Typography>
            </Stack>
          </>
        )}
      </TableContainer>

      <TablePaginationCustom
        count={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={TAB_TABLE_ROWS_PER_PAGE_OPTIONS}
        sx={TAB_TABLE_PAGINATION_SX}
      />
    </>
  );
}
